import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/dashboard';
import { StakeholderForm } from '../../components/stakeholders/StakeholderForm';
import { stakeholderService } from '../../services/stakeholderService';
import { CreateStakeholderDto, Stakeholder } from '../../types/stakeholder';
import { assignmentService } from '../../services/assignmentService';
import { mediaService } from '../../services/mediaService';
import { useAuth } from '../../contexts/AuthContext';
import { canUpdateStakeholder } from '../../utils/permissions';
import { upsertInfluenceScores } from '../../services/influencesService';
import { useToast } from '../../components/common';
import { ChevronLeft, CircleAlert, Lock } from 'lucide-react';
import { Button, Card, EmptyState, Page, PageHeader, Skeleton } from '../../components/ui';

export const StakeholderEditPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();

  const [stakeholder, setStakeholder] = useState<Stakeholder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [influenceScores, setInfluenceScores] = useState<{ categoryId: string; score: number }[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Warn before closing with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) return;
        setLoading(true);
        const data = await stakeholderService.getStakeholder(id);
        setStakeholder(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load stakeholder');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleUpdate = async (
    data: Partial<CreateStakeholderDto>,
    assignment?: { organizationId?: string; role?: string; department?: string; startDate?: string; endDate?: string; isCurrent?: boolean },
    extras?: { profileImageFile?: File | null; profileImageId?: string }
  ) => {
    if (!id) return;
    setSubmitting(true);

    try {
      // Step 1: Update stakeholder
      const updatePayload: any = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        professionalEmail: (data as any).professionalEmail,
        personalEmail: (data as any).personalEmail,
        professionalPhone: (data as any).professionalPhone,
        personalPhone: (data as any).personalPhone,
        facebookUrl: (data as any).facebookUrl,
        linkedinUrl: (data as any).linkedinUrl,
        twitterUrl: (data as any).twitterUrl,
        telegramUrl: (data as any).telegramUrl,
        whatsapp: (data as any).whatsapp,
        category: (data as any).category,
        location: data.location,
        bio: data.bio,
      };

      if (extras?.profileImageId && !extras.profileImageFile) {
        updatePayload.profileImageId = extras.profileImageId;
      }

      await stakeholderService.updateStakeholder(id, updatePayload);

      // Step 2: Save influence scores
      if (influenceScores && influenceScores.length) {
        try {
          await upsertInfluenceScores('stakeholder', id, influenceScores);
        } catch (e) {
          console.error('Failed to save influence scores', e);
          showWarning('Influence Scores', 'Stakeholder saved but influence scores could not be updated.');
        }
      }

      // Step 3: Upload profile image if new file provided
      if (extras?.profileImageFile) {
        try {
          const asset = await mediaService.uploadFile(extras.profileImageFile, { stakeholderId: id });
          await stakeholderService.updateStakeholder(id, { profileImageId: asset.id } as any);
        } catch (e) {
          console.error('Failed to upload profile image', e);
          showWarning('Profile Image', 'Stakeholder saved but profile image could not be uploaded.');
        }
      }

      // Step 4: Handle assignment updates
      if (assignment?.organizationId && assignment.role) {
        try {
          const all = await assignmentService.getByStakeholder(id);
          const startIso = assignment.startDate ? new Date(assignment.startDate).toISOString() : new Date().toISOString();
          const endIso = assignment.isCurrent ? undefined : (assignment.endDate ? new Date(assignment.endDate).toISOString() : undefined);

          const overlapping = (all || []).find(a => {
            if (a.organizationId !== assignment.organizationId) return false;
            const aStart = new Date(a.startDate).getTime();
            const aEnd = a.endDate ? new Date(a.endDate).getTime() : Number.POSITIVE_INFINITY;
            const bStart = new Date(startIso).getTime();
            const bEnd = endIso ? new Date(endIso).getTime() : Number.POSITIVE_INFINITY;
            return aStart <= bEnd && bStart <= aEnd;
          });

          if (overlapping) {
            await assignmentService.update(overlapping.id, {
              role: assignment.role,
              department: assignment.department,
              startDate: startIso,
              endDate: endIso,
              isCurrent: assignment.isCurrent ?? !endIso,
            });
          } else {
            const current = await assignmentService.getCurrentByStakeholder(id);
            const currentActive = (current || [])[0];
            if (currentActive && currentActive.organizationId !== assignment.organizationId) {
              const endNow = endIso || new Date().toISOString();
              await assignmentService.end(currentActive.id, endNow);
            }
            await assignmentService.create({
              stakeholderId: id,
              organizationId: assignment.organizationId,
              role: assignment.role,
              department: assignment.department,
              startDate: startIso,
              endDate: endIso,
              isCurrent: assignment.isCurrent ?? !endIso,
            });
          }
        } catch (e: any) {
          console.error('Failed to update assignment', e);
          showWarning('Assignment', 'Stakeholder saved but assignment could not be updated.');
        }
      }

      showSuccess('Stakeholder Updated', `${data.firstName} ${data.lastName} has been updated successfully.`);
      setIsDirty(false);
      navigate(`/stakeholders/${id}`);
    } catch (error: any) {
      console.error('Failed to update stakeholder', error);
      showError('Update Failed', error.response?.data?.message || error.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    }
    navigate(id ? `/stakeholders/${id}` : '/stakeholders');
  };

  const notAllowed = stakeholder && !canUpdateStakeholder(user, stakeholder);

  // Skeleton loading
  const renderSkeleton = () => (
    <div className="space-y-4" aria-busy="true">
      {[4, 4, 2].map((fields, i) => (
        <Card key={i} padded>
          <Skeleton className="mb-4 h-4 w-24" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Array.from({ length: fields }).map((_, j) => (
              <div key={j} className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-9 w-full rounded-xl" />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );

  const fullName = stakeholder ? `${stakeholder.firstName} ${stakeholder.lastName}`.trim() : '';

  return (
    <DashboardLayout>
      <Page className="pb-20 sm:pb-0">
        {/* Back goes through handleCancel so unsaved changes are confirmed */}
        <button
          type="button"
          onClick={handleCancel}
          className="-ml-1 mb-2 inline-flex h-7 max-w-full items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <ChevronLeft className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
          <span className="truncate">{fullName || 'Back'}</span>
        </button>
        <PageHeader title="Edit stakeholder" />

        {/* Content */}
        {loading ? (
          renderSkeleton()
        ) : error ? (
          <Card>
            <EmptyState
              icon={CircleAlert}
              title={error}
              action={
                <Button size="sm" variant="secondary" onClick={() => navigate('/stakeholders')}>
                  Stakeholders
                </Button>
              }
            />
          </Card>
        ) : notAllowed ? (
          <Card>
            <EmptyState
              icon={Lock}
              title="No permission to edit"
              action={
                <Button size="sm" variant="secondary" onClick={() => navigate(`/stakeholders/${id}`)}>
                  View
                </Button>
              }
            />
          </Card>
        ) : stakeholder ? (
          <StakeholderForm
            stakeholder={stakeholder}
            includeOrganizationFields
            onSave={(s, a, extras) => handleUpdate(s as Partial<CreateStakeholderDto>, a, extras)}
            influenceEntityId={id!}
            onInfluenceScoresChange={setInfluenceScores}
            isLoading={submitting}
            onCancel={handleCancel}
            onDirtyChange={setIsDirty}
          />
        ) : null}
      </Page>
    </DashboardLayout>
  );
};
